import { useUploadContext } from './UploadContext';
import { Button, Tooltip, Upload, UploadProps, message } from 'antd';
import { ReactComponent as ImageSvg } from './assets/image.svg';
import { ReactComponent as VideoSvg } from './assets/video.svg';
import { ReactComponent as UndoSvg } from './assets/undo.svg';
import { ReactComponent as RedoSvg } from './assets/redo.svg';
import Icon from '@ant-design/icons';
import { useUploadEditorStatic } from './withUpload';
import { useTranslate } from '@tolgee/react';
import validatorVideo from 'utils/validator/video';
import { uploadOssResource } from 'services/api/uploads';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { noop } from 'lodash-es';
import { Descendant } from 'slate';
import { ReactNode } from 'react';

const maxCount = 10;

function Toolbar({
  onChange,
  extraNode = null,
}: {
  onChange?: (value: Descendant[]) => void;
  extraNode?: ReactNode;
}) {
  const { t } = useTranslate();

  const editor = useUploadEditorStatic();

  const { imageList, videoList, setImageList, setVideoList } = useUploadContext();

  const commonUploadProps: UploadProps = {
    name: 'file',
    showUploadList: false,
    customRequest: ({ file, onProgress, onSuccess, onError }) => {
      uploadOssResource({
        file,
        onProgress,
      })
        .then((res) => {
          onSuccess?.(res);

          const targetIndex = editor.children.findIndex((child) => {
            return child.type === CustomSlateNodeType.UPLOAD && child.id === (file as any).uid;
          });

          if (targetIndex < 0) {
            return;
          }

          const { url } = res;

          onChange?.(
            editor.children.map((child, index) => {
              if (index !== targetIndex) {
                return child;
              }

              return {
                ...child,
                id: url,
              };
            }),
          );
        })
        .catch(onError || noop);
    },
  };

  const existImageCount = editor.children.reduce((res, cur) => {
    return res + (cur.type === CustomSlateNodeType.UPLOAD && cur.uploadType === 'IMAGE' ? 1 : 0);
  }, 0);

  const imageUpload = (
    <Upload
      disabled={existImageCount >= maxCount}
      accept={['jpg', 'jpeg', 'gif'].map((_) => `.${_}`).join(',')}
      fileList={imageList}
      onChange={({ fileList }) => setImageList(fileList)}
      beforeUpload={(file) => {
        const sizeLimit = 10 * 1024 * 1024;
        if (file.size > sizeLimit) {
          message.warn(
            t('upload.limitWarn', {
              size: `${(sizeLimit / 1024 / 1024).toFixed(2)}M`,
            }),
          );
          return Upload.LIST_IGNORE;
        }
        editor.insertUpload('IMAGE', file.uid);
      }}
      {...commonUploadProps}
    >
      <Button type="text" icon={<Icon component={ImageSvg} />} />
    </Upload>
  );

  const existVideoCount = editor.children.reduce((res, cur) => {
    return res + (cur.type === CustomSlateNodeType.UPLOAD && cur.uploadType === 'VIDEO' ? 1 : 0);
  }, 0);

  const videoUpload = (
    <Upload
      disabled={existVideoCount >= maxCount}
      accept=".mp4"
      fileList={videoList}
      onChange={({ fileList }) => setVideoList(fileList)}
      beforeUpload={(file) => {
        return validatorVideo(undefined, file)
          .catch(() => ({ errorText: '' }))
          .then(({ errorText }) => {
            if (errorText) {
              message.warn(errorText);
              return Upload.LIST_IGNORE;
            } else {
              editor.insertUpload('VIDEO', file.uid);
            }
          });
      }}
      {...commonUploadProps}
    >
      <Button type="text" icon={<Icon component={VideoSvg} />} />
    </Upload>
  );

  return (
    <>
      <Tooltip title={t('insert_image')}>{imageUpload}</Tooltip>
      <Tooltip title={t('insert_video')}>{videoUpload}</Tooltip>
      <Tooltip title={t('undo')}>
        <Button type="text" icon={<Icon component={UndoSvg} />} onClick={editor.undo} />
      </Tooltip>
      <Tooltip title={t('redo')}>
        <Button type="text" icon={<Icon component={RedoSvg} />} onClick={editor.redo} />
      </Tooltip>
      {extraNode}
    </>
  );
}

export default Toolbar;
